// EscapeHeader.jsx
// Reconstructs the persistent top strip every escape_peace-old page renders via
// shared includes (actions-default.php / seo-body.php) that live outside the
// accessible PHP folder: organization logo on the left, Back button on the right.
// Still isolated from 28 States theme state (`state.theme`) — but the logo now
// reads `state.ui.companyLogoUrl`, the same platform-wide (not theme-specific)
// value the existing login flow already builds from the organization API
// (Frontend/src/pages/loginSlice.js) and that `state.ui` already rehydrates
// from sessionStorage.userData.companyLogoUrl on every fresh page load — so
// this needs no new storage, and survives refresh for free. Falls back to the
// bundled REACT_APP_EM_LOGO platform default (same convention already used by
// src/pages/dddUI/user.jsx) whenever there's no organization logo, or if the
// organization logo URL fails to actually load.
//
// Publishes its own actually-rendered height as `--escape-header-height` on
// the document root. An organization logo can be any aspect ratio (unlike
// ExtraMile's own, previously assumed ~26px-tall one), so anything below the
// header that needs to clear it — currently only ScoreTimerBar.css's
// position:fixed Timer/Points row on the Game page — reads this variable
// instead of a hardcoded offset, so a tall client logo can never collide
// with content below the header.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import './EscapeHeader.css';

const EXTRA_MILE_HOME_URL = 'https://extramileplay.com/';

function EscapeHeader({ backTo, backHref }) {
  const companyLogoUrl = useSelector((state) => state.ui.companyLogoUrl);
  const fallbackLogoUrl = process.env.REACT_APP_EM_LOGO;
  const preferredLogoUrl = companyLogoUrl || fallbackLogoUrl || null;

  // Re-attempt the preferred (organization) logo whenever it changes (e.g. a
  // fresh login); if it later fails to load, drop to the fallback once, and
  // if even that fails, hide the image entirely rather than show a broken
  // image icon.
  const [logoSrc, setLogoSrc] = useState(preferredLogoUrl);
  useEffect(() => {
    setLogoSrc(preferredLogoUrl);
  }, [preferredLogoUrl]);

  const handleLogoError = () => {
    setLogoSrc((current) => (current !== fallbackLogoUrl ? fallbackLogoUrl || null : null));
  };

  const headerRef = useRef(null);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const publishHeight = () => {
      document.documentElement.style.setProperty('--escape-header-height', `${el.offsetHeight}px`);
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    return () => observer.disconnect();
    // Re-measure whenever the rendered logo changes (different image ->
    // different intrinsic size -> different header height once it loads).
  }, [logoSrc]);

  const isExternal = Boolean(backHref);

  return (
    <header className="escape-header" ref={headerRef}>
      <div className="escape-header-logo-holder">
        {logoSrc && (
          <a href={EXTRA_MILE_HOME_URL} className="escape-header-logo-link">
            <img
              src={logoSrc}
              alt="Company logo"
              className="escape-header-logo"
              onError={handleLogoError}
              onLoad={() => {
                // The ResizeObserver already catches this in real browsers, but
                // jsdom/tests don't always report intrinsic image size changes
                // through it — an explicit re-measure keeps behavior identical.
                if (headerRef.current) {
                  document.documentElement.style.setProperty(
                    '--escape-header-height',
                    `${headerRef.current.offsetHeight}px`
                  );
                }
              }}
            />
          </a>
        )}
      </div>
      <div className="escape-header-back-holder">
        {isExternal ? (
          <a href={backHref}>
            <button type="button" className="escape-header-back-btn">Back</button>
          </a>
        ) : (
          <Link to={backTo}>
            <button type="button" className="escape-header-back-btn">Back</button>
          </Link>
        )}
      </div>
    </header>
  );
}

export default EscapeHeader;